import type { JSX } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system/components/actions';
import { InlineMessage } from '@change-corgi/design-system/components/alerts';
import { Box } from '@change-corgi/design-system/layout';
import { createModalHook } from '@change-corgi/design-system/modals';
import { Text } from '@change-corgi/design-system/typography';

import type { PartialComment } from '../types';

import { useDeleteComment } from './useDeleteComment';

function CommentDeleteErrorMessage(): JSX.Element {
	return (
		<InlineMessage size="large" variant="error" p={8} my={16}>
			<Translate value="fe.errors.generic_try_again" />
		</InlineMessage>
	);
}

// TODO: use a reusable confirm modal?
export const useCommentDeleteModal = createModalHook<
	{
		comment: PartialComment;
	},
	{ success: boolean }
>({
	name: 'CommentDeleteModal',
	title: () => <Translate value="fe.components.comment.delete_comment_modal.title" />,
	body: function CommentDeleteModalBody({ options: { comment }, closeModal }) {
		const {
			data: { error },
			actions: { deleteComment },
		} = useDeleteComment(comment);

		return (
			<>
				<Text as="div">
					<Translate value="fe.components.comment.delete_comment_modal.subtitle" />
				</Text>
				{error && <CommentDeleteErrorMessage />}
				<Box mt={16}>
					<Button
						size="small"
						onClick={async () => {
							if (await deleteComment()) {
								closeModal({ success: true });
							}
						}}
						data-qa="confirm-delete-button"
						mr={16}
					>
						<Translate value="fe.components.confirm_modal.buttons.delete" />
					</Button>
					<Button
						variant="secondary"
						size="small"
						onClick={() => closeModal({ success: false })}
						data-qa="cancel-delete-button"
					>
						<Translate value="fe.components.confirm_modal.buttons.cancel" />
					</Button>
				</Box>
			</>
		);
	},
});
